import { AxiosResponse } from 'axios'
import { useMutation, useQueryList, type PBPListResponse } from '@percent/utility'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import type { getAccountsProps } from '@percent/cause-dashboard/services/iam/iam.types'
import type { IamGetAccounts } from '@percent/cause-dashboard/services/types/iamResponses.types'
import { useAuthState } from '@percent/cause-dashboard/common/hooks'
import { useCallback, useMemo, useState } from 'react'
import { Assignee } from '@percent/lemonade'

export const useTaskAssignees = () => {
  const { authState } = useAuthState()
  const { iamService, grantsService } = useServices()

  const [{ data: accountsData }] = useQueryList<getAccountsProps, IamGetAccounts>(
    ['iam', 'accounts'],
    params => iamService.getAccounts(params) as Promise<AxiosResponse<PBPListResponse<IamGetAccounts>>>,
    { pageSize: 100 }
  )

  const organisationOwners = useMemo(
    () => accountsData?.filter(account => account.role?.id === 'org:owner') ?? [],
    [accountsData]
  )
  const [searchValue, setSearchValue] = useState('')
  const [optimisticAssignments, setOptimisticAssignments] = useState<Record<string, string>>({})

  const filteredOwners = useMemo(
    () => organisationOwners.filter(account => account.email.toLowerCase().includes(searchValue.toLowerCase())),
    [organisationOwners, searchValue]
  )

  const getAssignee = useCallback(
    (taskId: string, assignedTo?: string) => {
      const assigneeId = optimisticAssignments[taskId] ?? assignedTo
      return assigneeId ? organisationOwners.find(owner => owner.id === assigneeId) : undefined
    },
    [organisationOwners, optimisticAssignments]
  )

  const [, { mutateAsync: mutateAssignee }] = useMutation(grantsService.updateTaskAssignee, () => setSearchValue(''))

  const updateTaskAssignee = async (taskId: string, assignee: Assignee) => {
    setOptimisticAssignments(prev => ({ ...prev, [taskId]: assignee.id }))

    try {
      await mutateAssignee({ taskId, assignedTo: assignee.id })
    } catch {
      setOptimisticAssignments(prev => {
        const { [taskId]: _, ...rest } = prev
        return rest
      })
    }
  }

  return {
    currentUser: {
      email: authState?.user?.email ?? '',
      id: authState?.user?.id ?? ''
    },
    organisationOwners: filteredOwners,
    search: { value: searchValue, onChange: setSearchValue },
    updateTaskAssignee,
    getAssignee
  }
}
